import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  fetchFieldServices,
  createFieldService,
  updateFieldService,
  deleteFieldService,
  fetchCustomers,
  fetchTechnicians,
  fetchVehicles,
  FieldService,
} from "@/lib/reseller-api";

const statusColors: Record<string, string> = {
  scheduled: "bg-blue-500",
  in_progress: "bg-yellow-500",
  completed: "bg-green-500",
  cancelled: "bg-gray-500",
};

export default function FieldServices() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<FieldService | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: services = [], isLoading } = useQuery({
    queryKey: ["field-services"],
    queryFn: fetchFieldServices,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
  });

  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles"],
    queryFn: fetchVehicles,
  });

  const { data: technicians = [] } = useQuery({
    queryKey: ["technicians"],
    queryFn: fetchTechnicians,
  });

  const createMutation = useMutation({
    mutationFn: createFieldService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-services"] });
      setIsOpen(false);
      toast({ title: t("reseller.fieldService.created") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FieldService> }) => updateFieldService(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-services"] });
      setIsOpen(false);
      setEditing(null);
      toast({ title: t("reseller.fieldService.updated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFieldService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-services"] });
      toast({ title: t("reseller.fieldService.deleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const filtered = services.filter(
    (s) =>
      s.customer?.name.toLowerCase().includes(search.toLowerCase()) ||
      s.vehicle?.plate_number?.toLowerCase().includes(search.toLowerCase()) ||
      s.technician?.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const vehicleId = formData.get("vehicle_id") as string;
    const technicianId = formData.get("technician_id") as string;
    const data = {
      customer_id: formData.get("customer_id") as string,
      vehicle_id: vehicleId && vehicleId !== "none" ? vehicleId : null,
      technician_id: technicianId && technicianId !== "none" ? technicianId : null,
      service_type: formData.get("service_type") as FieldService["service_type"],
      status: formData.get("status") as FieldService["status"],
      scheduled_date: formData.get("scheduled_date") as string || null,
      completed_date: formData.get("completed_date") as string || null,
      description: formData.get("description") as string,
      address: formData.get("address") as string,
      notes: formData.get("notes") as string,
    };

    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t("reseller.fieldServices")}</h1>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> {t("reseller.fieldService.create")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? t("reseller.fieldService.edit") : t("reseller.fieldService.create")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_id">{t("reseller.fieldService.customer")} *</Label>
                    <Select name="customer_id" required defaultValue={editing?.customer_id}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.fieldService.customer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle_id">{t("reseller.fieldService.vehicle")}</Label>
                    <Select name="vehicle_id" defaultValue={editing?.vehicle_id || "none"}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.fieldService.noVehicle")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("reseller.fieldService.noVehicle")}</SelectItem>
                        {vehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id}>{v.plate_number || v.name || `Vehicle ${v.id.slice(0, 8)}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="service_type">{t("reseller.fieldService.serviceType")} *</Label>
                    <Select name="service_type" required defaultValue={editing?.service_type || "installation"}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="installation">{t("reseller.fieldService.types.installation")}</SelectItem>
                        <SelectItem value="repair">{t("reseller.fieldService.types.repair")}</SelectItem>
                        <SelectItem value="maintenance">{t("reseller.fieldService.types.maintenance")}</SelectItem>
                        <SelectItem value="removal">{t("reseller.fieldService.types.removal")}</SelectItem>
                        <SelectItem value="inspection">{t("reseller.fieldService.types.inspection")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="technician_id">{t("reseller.fieldService.technician")}</Label>
                    <Select name="technician_id" defaultValue={editing?.technician_id || "none"}>
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.fieldService.unassigned")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("reseller.fieldService.unassigned")}</SelectItem>
                        {technicians.filter((tech) => tech.is_available).map((tech) => (
                          <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">{t("reseller.fieldService.status")}</Label>
                    <Select name="status" defaultValue={editing?.status || "scheduled"}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scheduled">{t("reseller.fieldService.statuses.scheduled")}</SelectItem>
                        <SelectItem value="in_progress">{t("reseller.fieldService.statuses.in_progress")}</SelectItem>
                        <SelectItem value="completed">{t("reseller.fieldService.statuses.completed")}</SelectItem>
                        <SelectItem value="cancelled">{t("reseller.fieldService.statuses.cancelled")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scheduled_date">{t("reseller.fieldService.scheduledDate")}</Label>
                    <Input
                      id="scheduled_date"
                      name="scheduled_date"
                      type="datetime-local"
                      defaultValue={editing?.scheduled_date?.slice(0, 16) || ""}
                    />
                  </div>
                </div>
                {editing && (
                  <div className="space-y-2">
                    <Label htmlFor="completed_date">{t("reseller.fieldService.completedDate")}</Label>
                    <Input
                      id="completed_date"
                      name="completed_date"
                      type="datetime-local"
                      defaultValue={editing?.completed_date?.slice(0, 16) || ""}
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="address">{t("reseller.fieldService.address")}</Label>
                  <Input id="address" name="address" defaultValue={editing?.address || ""} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t("reseller.fieldService.description")}</Label>
                  <Textarea id="description" name="description" defaultValue={editing?.description || ""} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">{t("reseller.fieldService.notes")}</Label>
                  <Textarea id="notes" name="notes" defaultValue={editing?.notes || ""} />
                </div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editing ? t("common.update") : t("common.create")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={t("reseller.fieldService.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.fieldService.noServices")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("reseller.fieldService.serviceType")}</TableHead>
                    <TableHead>{t("reseller.fieldService.customer")}</TableHead>
                    <TableHead>{t("reseller.fieldService.vehicle")}</TableHead>
                    <TableHead>{t("reseller.fieldService.technician")}</TableHead>
                    <TableHead>{t("reseller.fieldService.scheduledDate")}</TableHead>
                    <TableHead>{t("reseller.fieldService.status")}</TableHead>
                    <TableHead className="w-[100px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((service) => (
                    <TableRow key={service.id}>
                      <TableCell>{t(`reseller.fieldService.types.${service.service_type}`)}</TableCell>
                      <TableCell>{service.customer?.name || "-"}</TableCell>
                      <TableCell>{service.vehicle?.plate_number || "-"}</TableCell>
                      <TableCell>{service.technician?.name || t("reseller.fieldService.unassigned")}</TableCell>
                      <TableCell>{formatDateTime(service.scheduled_date)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[service.status]}>{t(`reseller.fieldService.statuses.${service.status}`)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => { setEditing(service); setIsOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("reseller.common.deleteConfirm"))) deleteMutation.mutate(service.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
